import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Building2, CornerDownLeft, CreditCard, Search, Users, type LucideIcon } from 'lucide-react';
import { listHotels, listPlans, listUsers } from '@/lib/api/admin';
import { cn } from '@/lib/cn';
import { useDebounced } from '@/lib/hooks';
import { Kbd } from '@/components/ui/page';
import { NAV_FLAT } from './nav';

/**
 * ⌘K. Pages first; then, once typed, hotels, users and plans by name — the
 * three lists the panel already holds, so a match is a cache hit and a miss
 * is one fetch. Selecting a row opens that row's drawer on its page.
 */
interface Item {
  key: string;
  group: 'Разделы' | 'Отели' | 'Пользователи' | 'Тарифы';
  icon: LucideIcon;
  label: string;
  hint?: string;
  to: string;
}

function score(text: string, q: string): number {
  const t = text.toLowerCase();
  if (t === q) return 3;
  if (t.startsWith(q)) return 2;
  if (t.includes(q)) return 1;
  return 0;
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  // Mounted only while open, so every opening starts from a blank query and
  // the first row — no state to reset.
  return open ? <Palette onClose={onClose} /> : null;
}

function Palette({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const q = useDebounced(query.trim().toLowerCase(), 80);

  // The three lists the panel already holds: a cache hit after the first visit, one fetch otherwise.
  const hotels = useQuery({ queryKey: ['admin', 'hotels'], queryFn: listHotels });
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: listUsers });
  const plans = useQuery({ queryKey: ['admin', 'plans'], queryFn: listPlans });

  const items = useMemo<Array<Item & { header: boolean }>>(() => {
    const pages: Item[] = NAV_FLAT.map((n) => ({ key: `p:${n.to}`, group: 'Разделы', icon: n.icon, label: n.label, to: n.to }));
    const ranked = <T,>(rows: T[], text: (r: T) => string, make: (r: T) => Item) =>
      rows.map((r) => ({ s: score(text(r), q), r })).filter((x) => x.s > 0).sort((a, b) => b.s - a.s).slice(0, 6).map((x) => make(x.r));
    const list: Item[] = !q ? pages : [
      ...pages.filter((p) => score(p.label, q) > 0),
      ...ranked(hotels.data ?? [], (h) => `${h.name} ${h.city}`, (h) => ({ key: `h:${h.id}`, group: 'Отели', icon: Building2, label: h.name, hint: `${h.city} · ${h.totalRooms} ном.`, to: `/hotels/${h.id}` })),
      ...ranked(users.data ?? [], (u) => `${u.displayName} ${u.email}`, (u) => ({ key: `u:${u.id}`, group: 'Пользователи', icon: Users, label: u.displayName || u.email, hint: u.email, to: `/users?id=${u.id}` })),
      ...ranked(plans.data ?? [], (p) => `${p.name} ${p.code}`, (p) => ({ key: `pl:${p.id}`, group: 'Тарифы', icon: CreditCard, label: p.name, hint: p.code, to: `/plans?edit=${p.id}` })),
    ];
    return list.map((it, i) => ({ ...it, header: i === 0 || list[i - 1].group !== it.group }));
  }, [q, hotels.data, users.data, plans.data]);

  // The list can shrink under the cursor; the cursor follows it down.
  const cursor = Math.min(selected, Math.max(0, items.length - 1));

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-index="${cursor}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  const go = (item: Item) => { onClose(); navigate(item.to); };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(Math.min(items.length - 1, cursor + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelected(Math.max(0, cursor - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); const it = items[cursor]; if (it) go(it); }
    else if (e.key === 'Escape') onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-[12vh] backdrop-blur-[2px]" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-label="Быстрый переход" className="w-full max-w-xl overflow-hidden rounded-2xl border border-line bg-surface shadow-2xl animate-pop" onKeyDown={onKey}>
        <div className="flex items-center gap-3 border-b border-line px-4">
          <Search size={18} className="text-faint" />
          <input
            autoFocus
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(0); }}
            placeholder="Раздел, отель, пользователь или тариф…"
            aria-label="Поиск"
            className="h-12 flex-1 bg-transparent text-sm text-ink outline-none"
          />
          <Kbd>Esc</Kbd>
        </div>
        <div ref={listRef} className="scroll-thin max-h-[50vh] overflow-y-auto p-2">
          {items.length === 0 && <p className="px-3 py-8 text-center text-sm text-muted">Ничего не найдено</p>}
          {items.map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={item.key}>
                {item.header && <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-faint">{item.group}</p>}
                <button
                  type="button"
                  data-index={i}
                  onMouseEnter={() => setSelected(i)}
                  onClick={() => go(item)}
                  className={cn('flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm', i === cursor ? 'bg-accent-soft text-accent-ink' : 'text-ink')}
                >
                  <Icon size={16} className={i === cursor ? 'text-accent-ink' : 'text-muted'} />
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                  {item.hint && <span className="truncate text-xs text-muted">{item.hint}</span>}
                  {i === cursor && <CornerDownLeft size={13} className="shrink-0 opacity-60" />}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
